import React, { useCallback, useEffect, useMemo } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button } from '@interco/inter-ui/components/Button'
import { Alert } from '@interco/inter-ui/components/Alert'
import Help from '@interco/icons/core/status/ic_help_circle'
import { ApplicationState } from '@store/types'
import { isHolidayDate, validaEnvHomeByBridge } from '@utils/functions'
import { BridgeService } from '@services/bridge'
import { Page } from '@templates'
import { clearStore } from '@store/refinanciamento/documentos/actions'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { SuccessIcon } from '@signals'
import { ColorWrapper, TextParagraph, GridStyled, Separator } from '@atoms'
import { Colors } from '@utils/Colors'
import Tag from '@utils/Tag'
import { useTrackingRefin } from '@hooks'
import { StepRefin } from '@utils/enums'
import routes from '@routes/routes'

import { SUCCESS_TIMELINE_STEPS, getPropostasContratosUrl } from './constants'
import {
  Container,
  HelpButton,
  Timeline,
  TimelineContent,
  TimelineDescription,
  TimelineItem,
  TimelineMarker,
  TimelineTitle,
  TutorialLink,
  WebHeader,
} from './styles'

const Sucesso = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useTrackingRefin(StepRefin.SUCESSO)

  const { isApp, origemGoBack } = useSelector((state: ApplicationState) => state.ui.navigation)

  const showHolidayAlert = useMemo(() => isHolidayDate(), [])

  const finish = useCallback(() => {
    dispatch(clearStore())
  }, [dispatch])

  const handleFAQClick = useCallback(() => {
    navigate(routes.FAQ)
  }, [navigate])

  const handleTutorialClick = useCallback(() => {
    navigate(routes.TUTORIAL, { state: { from: routes.SUCESSO } })
  }, [navigate])

  useEffect(() => {
    BridgeService.tac({
      name: 'tela_sucesso',
      actions: ['confirmar_contratacao'],
    })
  }, [])

  useEffect(() => {
    finish()
  }, [finish])

  useEffect(() => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:success',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: 'Refinanciamento solicitado',
        section: 'success',
        sub_section: '',
        step: '',
        scenario: 'Refinanciamento solicitado',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Refinanciamento solicitado',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:feedback',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'success',
      })
    }
  }, [isApp])

  const renderTimeline = () => (
    <Timeline aria-label="Próximos passos do refinanciamento">
      {SUCCESS_TIMELINE_STEPS.map((step, index) => (
        <TimelineItem key={step.title}>
          <TimelineMarker aria-hidden="true">{index + 1}</TimelineMarker>
          <TimelineContent>
            <TimelineTitle>{step.title}</TimelineTitle>
            <TimelineDescription>{step.description}</TimelineDescription>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  )

  return (
    <Page
      stickyContainerFooterBottom="30px"
      showLeftIcon={false}
      showRightIcon
      rightIcon="question-mark"
      onRightButtonClick={handleFAQClick}
      titleHeader="Refinanciamento"
      positionFooter="sticky"
      stickyFooter={
        isApp ? (
          <>
            <Button
              onClick={() => {
                dispatch(trackingRefin(StepRefin.CLICK_SUCESSO_IR_HOME))
                BridgeService.tic({
                  name: 'btn_ir_home',
                  action: 'ir_para_home',
                })

                if (isApp) {
                  Tag.enviarTagueamentoApp({
                    screen: 'clt:refin:success',
                    ref_type: 'botao',
                    content_action: 'Toque',
                    action_id: 'Refinanciamento solicitado',
                    section: 'success',
                    sub_section: '',
                    step: '',
                    scenario: 'Refinanciamento solicitado',
                    ref_figma: '2',
                  })
                } else {
                  Tag.enviarTagueamentoWeb({
                    element_action: 'click button',
                    element_name: 'Refinanciamento solicitado',
                    c_page: window.location.href,
                    redirect_url: '',
                    section_name: 'payrollloan:refin:feedback',
                    section: 'feedback',
                    sub_section: '',
                    step: '',
                    scenario: 'success',
                  })
                }

                window.location.href = validaEnvHomeByBridge(String(origemGoBack), isApp) || ''
              }}
            >
              Ir para a home
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                dispatch(trackingRefin(StepRefin.CLICK_SUCESSO_ACOMPANHAR_PROPOSTA))
                BridgeService.tic({
                  name: 'btn_acompanhar_proposta',
                  action: 'acompanhar_proposta',
                })

                if (isApp) {
                  Tag.enviarTagueamentoApp({
                    screen: 'clt:refin:success',
                    ref_type: 'botao',
                    content_action: 'Toque',
                    action_id: 'Acompanhar proposta',
                    section: 'success',
                    sub_section: '',
                    step: '',
                    scenario: 'Refinanciamento solicitado',
                    ref_figma: '2',
                  })
                } else {
                  Tag.enviarTagueamentoWeb({
                    element_action: 'click button',
                    element_name: 'Refinanciamento solicitado',
                    c_page: window.location.href,
                    redirect_url: '',
                    section_name: 'payrollloan:refin:feedback',
                    section: 'feedback',
                    sub_section: '',
                    step: '',
                    scenario: 'success',
                  })
                }

                window.location.href = getPropostasContratosUrl(isApp)
              }}
            >
              Acompanhar proposta
            </Button>
          </>
        ) : (
          <Button
            onClick={() => {
              dispatch(trackingRefin(StepRefin.CLICK_SUCESSO_ABRIR_CONTA))
              BridgeService.tic({
                name: 'btn_abrir_conta',
                action: 'abrir_conta',
              })

              if (isApp) {
                Tag.enviarTagueamentoApp({
                  screen: 'clt:refin:success',
                  ref_type: 'botao',
                  content_action: 'Toque',
                  action_id: 'Abrir conta',
                  section: 'success',
                  sub_section: '',
                  step: '',
                  scenario: 'Refinanciamento solicitado',
                  ref_figma: '2',
                })
              } else {
                Tag.enviarTagueamentoWeb({
                  element_action: 'click button',
                  element_name: 'Abrir conta',
                  c_page: window.location.href,
                  redirect_url: '',
                  section_name: 'payrollloan:refin:feedback',
                  section: 'feedback',
                  sub_section: '',
                  step: '',
                  scenario: 'success',
                })
              }

              window.location.href = process.env.REACT_APP_CONTA_DIGITAL || ''
            }}
          >
            Abra sua conta
          </Button>
        )
      }
    >
      <Container>
        {!isApp && (
          <WebHeader>
            <ColorWrapper color={Colors.GRAY500} fontWeight="600">
              <TextParagraph variant="headline-h3">Refinanciamento</TextParagraph>
            </ColorWrapper>
            <HelpButton
              type="button"
              aria-label="Ajuda - perguntas frequentes"
              onClick={handleFAQClick}
            >
              <Help color={Colors.PRIMARY500} width={24} height={24} />
            </HelpButton>
          </WebHeader>
        )}
        <GridStyled justify="space-around">
          <SuccessIcon />
        </GridStyled>
        <ColorWrapper
          color={Colors.GRAY500}
          fontWeight="600"
          margin="22px 0 16px 0"
          style={{ textAlign: 'center' }}
        >
          <TextParagraph variant="headline-h1">Refinanciamento solicitado</TextParagraph>
        </ColorWrapper>
        <Separator margin="24px 0" variant="small" style={{ backgroundColor: Colors.GRAY100 }} />
        <ColorWrapper color={Colors.GRAY500} fontWeight="600" margin="0 0 16px 0">
          <TextParagraph variant="headline-h3" textAlign="center">
            O que acontece a partir de agora
          </TextParagraph>
        </ColorWrapper>
        {renderTimeline()}
        <TutorialLink type="button" onClick={handleTutorialClick}>
          Saiba mais sobre o refinanciamento
        </TutorialLink>
        {!isApp ? (
          <>
            <Separator margin="24px -24px" style={{ backgroundColor: Colors.GRAY100 }} />
            <ColorWrapper fontWeight="600">
              <TextParagraph variant="headline-h1">Abra sua conta</TextParagraph>
            </ColorWrapper>
            <ColorWrapper fontWeight="600" margin="0 0 16px 0">
              <TextParagraph variant="headline-h3">Gratuita, simples e completa</TextParagraph>
            </ColorWrapper>
            <ColorWrapper color={Colors.GRAY500} margin="0 0 32px 0">
              <TextParagraph variant="caption-1">
                Simplifique toda a sua vida com uma conta digital completa e um Super App com
                shopping, investimento, recarga e muito mais!
              </TextParagraph>
            </ColorWrapper>
          </>
        ) : (
          <></>
        )}
        {showHolidayAlert ? (
          <Alert title="Pagamentos durante o feriado" type="warning">
            Contratações realizadas durante o feriado de carnaval terão o pagamento liberado a
            partir de 18/03
          </Alert>
        ) : (
          <></>
        )}
      </Container>
    </Page>
  )
}
export default Sucesso
